import React from 'react';

import { defaultOperators, defaultFormatters, defaultRenderers } from 'components/Grid';

import { ColumnDefinition } from 'components/Grid/types';

import EntityRelatedActionsCell from 'components/SharedFormComponents/EntityRelatedActionsCell';

import { EntityRelatedActionsType } from 'utils/api/modules/entities/entities.types';

import {
  AuditStatus,
  AuditStatusNames,
  FULL_DATETIME_COLUMN_MIN_WIDTH,
  FULL_DATETIME_FORMAT,
  ProtectorType,
} from 'core/constants';

import { AuditStatusOperator } from 'components/Grid/operators/Status/Status';

// AUDITS datetime answers are ISO strings, not the epoch ints Events uses — never route them through an epoch path.
import { formatMoment } from 'utils/formatters/timezone';

import AuditStatusChip from 'components/AuditStatusChip';
import { formatScorePercentage } from 'components/Audits/AuditScoreSummary';

import { StyledCell } from './styles';

// The visible default set and its order follow the rebuild prototype's list (Name, Location, Auditor,
// Tasks, Status, date). The entity-native columns the spec added stay available from the column selector.
export const basicAuditsColumns = [
  {
    field: 'audits__name',
    headerName: 'Name',
    flex: 2,
    protectorType: ProtectorType.ShortText,
  },
  {
    field: 'audits__id',
    headerName: 'ID',
    defaultVisibility: false,
    filterable: false,
    protectorType: ProtectorType.Number,
  },
  {
    field: 'formName',
    headerName: 'Template',
    defaultVisibility: false,
    nativeFilter: true,
    sortable: true,
  },
  {
    // Value is `{ lat, lng, address }`: it filters and sorts on `$.value.address`, the string shown
    // in the cell.
    field: 'audits__location',
    headerName: 'Location',
    flex: 1,
    renderCell: defaultRenderers.location,
    valueGetter: (value: unknown) => ({ value }),
    valueFormatter: defaultFormatters.location,
    filterOperators: defaultOperators.location(),
    protectorType: ProtectorType.Location,
  },
  {
    // Object-valued, so it filters on `$.value.uuid` — "is" only, uuid filters have no
    // empty/not-empty branch — and sorts on `$.value.name`.
    field: 'audits__auditor',
    headerName: 'Auditor',
    flex: 2,
    renderCell: defaultRenderers.user,
    valueGetter: (value: unknown) => ({ value }),
    filterOperators: defaultOperators.user(),
    protectorType: ProtectorType.User,
  },
  {
    field: 'relatedActions',
    headerName: 'Tasks',
    flex: 1,
    maxWidth: 100,
    renderCell: ({ value }: { value: EntityRelatedActionsType | null }) => (
      <EntityRelatedActionsCell relatedActions={value || { total: 0, completed: 0 }} />
    ),
    valueFormatter: (value: EntityRelatedActionsType | null) => `completed:${value?.completed};total:${value?.total}`,
    sortable: false,
    filterable: false,
  },
  {
    // Unsortable: derived server-side from the latest AuditState, so there's no field value to order by.
    field: 'auditStatus',
    headerName: 'Status',
    flex: 1,
    sortable: false,
    nativeFilter: true,
    filterOperators: AuditStatusOperator(),
    renderCell: ({ value }: { value: AuditStatus | null }) => <AuditStatusChip status={value} />,
    valueFormatter: (value: AuditStatus | null) =>
      value === AuditStatus.Submitted
        ? AuditStatusNames[AuditStatus.Submitted]
        : AuditStatusNames[AuditStatus.InProgress],
  },
  {
    field: 'audits__score',
    headerName: 'Score',
    defaultVisibility: false,
    protectorType: ProtectorType.Number,
    renderCell: ({ value }: { value: number | null }) => (
      <StyledCell>{value == null ? '' : formatScorePercentage(value)}</StyledCell>
    ),
    valueFormatter: (value: number | null) => (value == null ? '' : formatScorePercentage(value)),
  },
  {
    field: 'audits__dateCompleted',
    headerName: 'Date completed',
    flex: 1,
    minWidth: FULL_DATETIME_COLUMN_MIN_WIDTH,
    sortable: true,
    filterOperators: defaultOperators.date_time(),
    protectorType: ProtectorType.DateTime,
    renderCell: ({ value, row }: { value: string | null; row: { auditStatus: AuditStatus | null } }) => (
      <StyledCell data-testid="audits-date-completed">
        {value == null && row.auditStatus !== AuditStatus.Submitted
          ? 'Not submitted'
          : formatMoment(value, FULL_DATETIME_FORMAT)}
      </StyledCell>
    ),
  },
  {
    field: 'createdAt',
    headerName: 'Created',
    defaultVisibility: false,
    minWidth: FULL_DATETIME_COLUMN_MIN_WIDTH,
    renderCell: ({ value }: { value: string }) => (
      <StyledCell data-testid="audits-created-at">{formatMoment(value, FULL_DATETIME_FORMAT)}</StyledCell>
    ),
    filterOperators: defaultOperators.date_time(),
    valueFormatter: (value: string | null) => formatMoment(value, FULL_DATETIME_FORMAT),
    nativeFilter: 'createdAt',
    protectorType: ProtectorType.DateTime,
  },
] as unknown as ColumnDefinition[];

/** The columns hidden for the prototype's default set; Created was hidden before and stays so. */
const PROTOTYPE_HIDDEN_COLUMNS = new Set(['audits__id', 'formName', 'audits__score']);

/** A hidden-by-default column is unreachable for a role without the column selector, so that role sees these. */
export const auditsColumnsWithoutSelector: ColumnDefinition[] = basicAuditsColumns.map((column) =>
  PROTOTYPE_HIDDEN_COLUMNS.has(column.field) ? { ...column, defaultVisibility: true } : column
);
